// Правило гейта обязательных post-login flow — чистая функция без React.
//
// Живёт отдельным файлом не ради красоты: «двойной онбординг» уже случался
// (человек проходил весь онбординг второй раз из-за гонки рефетча `auth.me`),
// и правило, зашитое в тело компонента, проверить можно только руками. Здесь
// оно проверяется тестом.
//
// Шагов теперь три, и первый из них — ЧУЖОЙ: блокирующая редакция документа
// экосистемы принимается в Legal Center IEC ID, а не у нас
// (docs/INTEGRATION-GUIDE.md §3.2). Поэтому цель гейта перестала быть просто
// строкой-путём: она либо наш маршрут, либо внешний адрес.

// Согласие форсится везде, кроме самого consent-экрана + читаемых юр-страниц
// (footer-ссылки) + auth-flow (logout с экрана decline уходит на /auth/login).
const CONSENT_EXEMPT_PREFIXES = ["/legal/", "/auth/"] as const;
// Онбординг дополнительно исключает сам онбординг-wizard и публичную страницу
// «Метод»: с экрана «Как здесь учатся» есть кнопка «Подробнее о методе», и без
// исключения она вела в никуда — гейт разворачивал обратно на онбординг, то
// есть кнопка выглядела сломанной. Страница публичная (в `PROTECTED_PREFIXES`
// её нет), читать её до завершения онбординга ничему не мешает; возврат на
// любой другой адрес сервиса снова приводит на онбординг.
const ONBOARDING_EXEMPT_PREFIXES = ["/legal/", "/auth/", "/onboarding", "/method"] as const;

/** Путь равен префиксу или лежит под ним. */
export function underPrefix(pathname: string, prefixes: readonly string[]): boolean {
	return prefixes.some((p) => pathname === p || pathname.startsWith(p));
}

// Адрес экосистемы объявлен в `lib/iec-id-portal.ts` — он нужен и слою без
// React. Реэкспорт, а не вторая копия: значение одно на приложение.
import { IEC_ID_AUTH_URL } from "@/lib/iec-id-portal";
import { withNativeAuthSurface } from "@/lib/native-auth-surface";

// Реэкспорт, а не вторая копия: значение одно на приложение.
export { IEC_ID_AUTH_URL };

/** Внутренние маршруты гейта. */
export type AuthFlowInternalTarget = "/legal/accept" | "/onboarding" | "/";

/**
 * Уход из приложения целиком: экран принятия живёт на их домене, `navigate()`
 * сюда не годится — нужен `window.location.assign`.
 */
export interface AuthFlowExternalTarget {
	externalUrl: string;
}

export type AuthFlowTarget = AuthFlowInternalTarget | AuthFlowExternalTarget | null;

export interface AuthFlowState {
	pathname: string;
	isLoading: boolean;
	isOffline: boolean;
	isAuthenticated: boolean;
	pendingConsent: boolean;
	pendingOnboarding: boolean;
	/**
	 * `users.iec_legal_pending` из `auth.me`: пришла блокирующая редакция
	 * документа ЭКОСИСТЕМЫ. Принять её у себя нельзя — только увести к ним.
	 */
	iecLegalPending: boolean;
	/**
	 * Уже уводили в этой загрузке страницы. Без этого признака гейт зацикливается:
	 * флаг гасится только успешным входом, а вернувшийся по `return_to` человек
	 * приходит с той же сессией и тем же `iec_legal_pending = true`, и его
	 * немедленно выкидывает обратно — пинг-понг между доменами. Один заход за
	 * загрузку страницы: если не помогло, человек попадает в приложение, а
	 * блокирующую редакцию всё равно не пропустит их гейт на `authorize`
	 * (§3.2 гайда) при следующем входе.
	 */
	iecConsentRedirectSent: boolean;
	/** `window.location.origin` — из него собирается абсолютный `return_to`. */
	origin: string;
}

/**
 * `return_to` обязан быть полным абсолютным `https`-адресом на наш домен —
 * относительный путь и чужой хост они молча игнорируют (§3.2), и человек
 * останется у них вместо возврата.
 */
export function buildIecConsentUrl(returnToAbsolute: string): string {
	// Признак нативной поверхности — как и на входе: экран согласий открывается
	// в WebView самого приложения, и баннер cookie на нём означал бы ровно то
	// замечание App Store (5.1.2(i)), ради которого признак заведён.
	// См. `lib/native-auth-surface.ts`.
	return withNativeAuthSurface(
		`${IEC_ID_AUTH_URL}/consent?return_to=${encodeURIComponent(returnToAbsolute)}`,
	);
}

/** Внешняя цель или внутренний маршрут — различать по форме, не по префиксу строки. */
export function isExternalAuthFlowTarget(target: AuthFlowTarget): target is AuthFlowExternalTarget {
	return typeof target === "object" && target !== null;
}

/** Куда обязан быть уведён пользователь с этого адреса. `null` — оставить как есть. */
export function resolveAuthFlowTarget(state: AuthFlowState): AuthFlowTarget {
	const { pathname, pendingConsent, pendingOnboarding } = state;
	// Офлайн cold start не выкидывает залогиненного на гейт; гость и время
	// загрузки сессии — не наше дело (гостя разбирает отдельная ветка гейта).
	if (state.isLoading || state.isOffline || !state.isAuthenticated) return null;

	// Документ экосистемы важнее нашего: без действующего основания обработки
	// пускать дальше нельзя вовсе, а наше согласие человек примет уже после
	// возврата. Те же исключения, что у `pendingConsent`: с юр-страниц и из
	// auth-flow (выход!) уводить нельзя — иначе разлогиниться станет негде.
	if (
		state.iecLegalPending &&
		!state.iecConsentRedirectSent &&
		!underPrefix(pathname, CONSENT_EXEMPT_PREFIXES)
	) {
		return { externalUrl: buildIecConsentUrl(`${state.origin}${pathname}`) };
	}

	if (pendingConsent) {
		return underPrefix(pathname, CONSENT_EXEMPT_PREFIXES) ? null : "/legal/accept";
	}
	if (pendingOnboarding && !underPrefix(pathname, ONBOARDING_EXEMPT_PREFIXES)) {
		return "/onboarding";
	}
	if (!pendingOnboarding && (pathname === "/onboarding" || pathname.startsWith("/onboarding/"))) {
		// Зеркало ветки /legal/accept ниже: онбординг уже завершён, но пользователь
		// на /onboarding/* — реактивно уводим на дашборд. Это самовосстановление
		// после гонки «stale-рефетч auth.me перезатёр pendingOnboarding=false»:
		// без этой ветки один ложный отскок на /onboarding заставлял проходить
		// весь онбординг второй раз (свежий рефетч уже принёс false, но обратного
		// увода не было — /onboarding в exempt-списке).
		//
		// Эта же ветка — штатный уход с экрана «Готово»: сам экран гасит флаг и
		// переходит, только если гейт молчит, иначе опередил бы контекст сессии
		// (`components/onboarding/_exit-sequence.ts`).
		//
		// Исключения из этой ветки больше нет. Раньше здесь жил `?replay=true`
		// для `/onboarding/tour` — добровольного повтора тура из меню профиля.
		// Тур удалён (пять открыток заменил экран «Метод»), повтор переехал на
		// модалку «Как это работает», а проверка `search.replay` осталась бы
		// дырой в самовосстановлении: любой адрес онбординга с этим параметром
		// в URL переставал уводить прошедшего пользователя на дашборд.
		return "/";
	}
	if (pathname === "/legal/accept") {
		// Consent уже принят, но пользователь всё ещё на /legal/accept. Сам экран
		// согласия в CONSENT/ONBOARDING_EXEMPT (чтобы не было петли при
		// pendingConsent), поэтому без этой ветки гейт не уведёт его дальше, а
		// гоночный manual-navigate из consent-flow (refresh auth.me ещё не доехал
		// до контекста) мог отскочить обратно сюда → пользователь застревает на
		// принятом согласии. Реактивно уводим: онбординг либо дашборд.
		return pendingOnboarding ? "/onboarding" : "/";
	}
	return null;
}
